import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articlesContent } from '@/mocks/articles-content';
import type { FullArticle, ArticleSection } from '@/mocks/articles-content';

function SectionContent({ section }: { section: ArticleSection }) {
  switch (section.type) {
    case 'h2':
      return (
        <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-900 mt-12 mb-4 first:mt-0">
          {section.content}
        </h2>
      );
    case 'h3':
      return (
        <h3 className="font-heading font-semibold text-lg text-foreground-900 mt-8 mb-3">
          {section.content}
        </h3>
      );
    case 'p':
      return (
        <p className="text-foreground-600 leading-relaxed mb-4" dangerouslySetInnerHTML={{ __html: section.content || '' }} />
      );
    case 'ul':
      return (
        <ul className="space-y-2 mb-4">
          {section.items?.map((item, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-foreground-600">
              <span className="text-primary-500 mt-0.5 flex-shrink-0">•</span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      );
    case 'ol':
      return (
        <ol className="space-y-3 list-decimal list-inside mb-4">
          {section.items?.map((item, i) => (
            <li key={i} className="text-sm text-foreground-600 leading-relaxed">
              {item}
            </li>
          ))}
        </ol>
      );
    case 'grid':
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-6">
          {section.gridItems?.map((item, i) => (
            <div key={i} className="p-5 rounded-xl bg-background-100 border border-background-200/70">
              <h4 className="font-heading font-semibold text-sm text-foreground-900 mb-2">{item.title}</h4>
              <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      );
    case 'tip':
      return (
        <div className="my-6 p-5 rounded-xl bg-accent-50 border border-accent-200/60">
          <div className="flex items-start gap-3">
            <div className="w-7 h-7 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-lightbulb-line text-accent-600 text-sm"></i>
            </div>
            <p className="text-sm text-accent-800 leading-relaxed">{section.content}</p>
          </div>
        </div>
      );
    case 'quote':
      return (
        <blockquote className="my-6 pl-5 border-l-3 border-primary-300 italic text-foreground-500">
          <p className="text-base leading-relaxed">{section.content}</p>
        </blockquote>
      );
    default:
      return null;
  }
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article: FullArticle | undefined = articlesContent.find((a) => a.slug === slug);
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://vigorbuyspread.cfd';

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-24 pb-16 md:pt-28 md:pb-24 bg-background-50 min-h-[60vh]">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 text-center">
            <h1 className="font-heading font-bold text-2xl text-foreground-900 mb-4">Article Not Found</h1>
            <p className="text-foreground-500 mb-6">The article you are looking for could not be located.</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white rounded-full text-sm font-semibold hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              Back to Home
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const articleUrl = `${siteUrl}/article/${article.slug}`;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.metaDescription,
    image: article.heroImage,
    url: articleUrl,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': articleUrl,
    },
    datePublished: '2026-08-02',
    dateModified: '2026-08-02',
    author: {
      '@type': 'Organization',
      name: 'VigorBuy Spreadsheet Resource Hub',
      url: siteUrl,
    },
    publisher: {
      '@type': 'Organization',
      name: 'VigorBuy Spreadsheet Resource Hub',
      url: siteUrl,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: siteUrl,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: article.title,
        item: articleUrl,
      },
    ],
  };

  return (
    <>
      <Navbar />
      <main>
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>

        <article className="pt-24 pb-16 md:pt-28 md:pb-24 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <nav className="mb-8" aria-label="Breadcrumb">
                <ol className="flex items-center gap-2 text-sm text-foreground-400 flex-wrap">
                  <li>
                    <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                      Home
                    </Link>
                  </li>
                  <li><i className="ri-arrow-right-s-line text-xs"></i></li>
                  <li className="text-foreground-600 truncate">{article.title}</li>
                </ol>
              </nav>

              <header className="mb-10">
                <div className="flex items-center gap-3 mb-4 flex-wrap">
                  <span className="px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold whitespace-nowrap">
                    Guide
                  </span>
                  <span className="text-xs text-foreground-400 whitespace-nowrap">August 2, 2026</span>
                </div>

                <h1 className="font-heading font-extrabold text-2xl md:text-4xl text-foreground-900 leading-tight mb-4">
                  {article.title}
                </h1>

                <p className="text-foreground-500 text-base md:text-lg leading-relaxed">
                  {article.metaDescription}
                </p>
              </header>

              <div className="aspect-[16/9] rounded-2xl overflow-hidden mb-10 bg-background-100 border border-background-200/70">
                <img
                  src={article.heroImage}
                  alt={article.title}
                  title={article.title}
                  className="w-full h-full object-cover"
                />
              </div>

              <div className="prose-custom space-y-0 text-foreground-600 leading-relaxed">
                {article.sections.map((section, idx) => (
                  <SectionContent key={idx} section={section} />
                ))}
              </div>

              <div className="mt-12 pt-8 border-t border-background-200/60">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div>
                    <p className="text-sm text-foreground-400 mb-1">Published on August 2, 2026</p>
                    <p className="text-sm text-foreground-400">VigorBuy Spreadsheet Resource Hub</p>
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <Link
                      to="/"
                      className="inline-flex items-center gap-2 px-5 py-2.5 bg-background-100 text-foreground-700 rounded-full text-sm font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-arrow-left-line"></i>
                      Back to Home
                    </Link>
                    <a
                      href="https://kaspreadsheet.com"
                      target="_blank"
                      rel="noopener nofollow"
                      className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Buy It Now
                      <i className="ri-arrow-right-up-line"></i>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}